import type { Option } from '@openx/types/options';
import type { TFunction } from 'i18next';
import { type DealPromptFilters, dealPromptFilterGender } from '../../../utils/types';

export const MAX_VISIBLE_APPLIED_FILTER_VALUES = 3;
export const DEMOGRAPHICS_AGE_MAX = 99;

export type AudienceAppliedFilterValue = {
  display: string;
  type?: string;
};

export type AudienceAppliedFilterDisplay = {
  label: string;
  values: AudienceAppliedFilterValue[];
};

type AudienceAppliedFilterOptions = {
  stateOptions?: Option[];
  dmaOptions?: Option[];
  congressionalDistrictOptions?: Option[];
  categoryOptions?: Option[];
  subCategoryOptions?: Option[];
  accountOptions?: Option[];
};

const resolveOptionName = (id: string, options?: Option[]): string =>
  options?.find(option => option.id === id)?.name ?? id;

const resolveOptionNames = (ids: string[], options?: Option[]): AudienceAppliedFilterValue[] =>
  ids.map(id => ({ display: resolveOptionName(id, options) }));

export const formatAgeRange = (age?: string[]): string | undefined => {
  if (!age?.length) {
    return undefined;
  }

  const [min, max] = age;

  if (min && max) {
    if (max === String(DEMOGRAPHICS_AGE_MAX)) {
      return `${min}+`;
    }

    return `${min} - ${max}`;
  }

  if (min) {
    return `${min}+`;
  }

  if (max) {
    return `< ${max}`;
  }

  return undefined;
};

export const formatDemographicsValue = (filters: DealPromptFilters, t: TFunction): string | undefined => {
  const parts: string[] = [];

  if (
    filters.gender &&
    (filters.gender === dealPromptFilterGender.MALE || filters.gender === dealPromptFilterGender.FEMALE)
  ) {
    parts.push(filters.gender === dealPromptFilterGender.MALE ? t('Male') : t('Female'));
  }

  const age = formatAgeRange(filters.age);

  if (age) {
    parts.push(age);
  }

  return parts.length > 0 ? parts.join(' ') : undefined;
};

const getUserLocationValues = (
  filters: DealPromptFilters,
  options: AudienceAppliedFilterOptions,
  t: TFunction
): AudienceAppliedFilterValue[] => [
  ...(filters.state ?? []).map(id => ({
    display: resolveOptionName(id, options.stateOptions),
    type: t('State'),
  })),
  ...(filters.dma ?? []).map(id => ({
    display: resolveOptionName(id, options.dmaOptions),
    type: t('DMA'),
  })),
  ...(filters.congressionalDistrict ?? []).map(id => ({
    display: resolveOptionName(id, options.congressionalDistrictOptions),
    type: t('Congressional District'),
  })),
];

export const getAudienceAppliedFiltersDisplay = (
  filters: DealPromptFilters | undefined,
  options: AudienceAppliedFilterOptions,
  t: TFunction
): AudienceAppliedFilterDisplay[] => {
  if (!filters) {
    return [];
  }

  const appliedFilters: AudienceAppliedFilterDisplay[] = [];

  const userLocationValues = getUserLocationValues(filters, options, t);

  if (userLocationValues.length > 0) {
    appliedFilters.push({
      label: t('User Location'),
      values: userLocationValues,
    });
  }

  const demographicsValue = formatDemographicsValue(filters, t);

  if (demographicsValue) {
    appliedFilters.push({
      label: t('Demographics'),
      values: [{ display: demographicsValue }],
    });
  }

  if (filters.uniqueUsersReach) {
    appliedFilters.push({
      label: t('Goal'),
      values: [{ display: `${filters.uniqueUsersReach}M` }],
    });
  }

  if (filters.category?.length) {
    appliedFilters.push({
      label: t('Category'),
      values: resolveOptionNames(filters.category, options.categoryOptions),
    });
  }

  if (filters.sub_category?.length) {
    appliedFilters.push({
      label: t('Sub Category'),
      values: resolveOptionNames(filters.sub_category, options.subCategoryOptions),
    });
  }

  if (filters.account?.length) {
    appliedFilters.push({
      label: t('Data Partner'),
      values: resolveOptionNames(filters.account, options.accountOptions),
    });
  }

  return appliedFilters;
};
